/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Aegis AO Soft LLC and Alexander Orlov.
 *
 * This code may be used, reproduced, modified, or distributed ONLY with the
 * prior written permission of Aegis AO Soft LLC / Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft LLC
 */


import apiClient from './client';

const BASE = '/ScrapeJobs';

export interface ScrapeJobsDashboard {
  totalJobs: number;
  queued: number;
  processing: number;
  completed: number;
  failed: number;
  skippedMfa: number;
  activeWorkers: string[];
  byProvider: ProviderStats[];
  byJobType: Record<string, number>;
  lastScheduledAt: string | null;
}

export interface ProviderStats {
  provider: string;
  total: number;
  completed: number;
  failed: number;
  processing: number;
  queued: number;
  avgDurationMs: number;
}

export interface ScrapeJob {
  id: number;
  jobType: string;
  ownerId: string;
  tollAccountId: string | null;
  pluginKey: string | null;
  carBatchStart: number | null;
  carBatchSize: number | null;
  scrapeFromUtc: string;
  scrapeToUtc: string;
  scheduledForUtc: string;
  priority: number;
  status: string;
  lockedBy: string | null;
  lockedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  attempts: number;
  maxAttempts: number;
  nextRetryAt: string | null;
  lastError: string | null;
  providerKey: string;
  transactionsFound: number;
  newTransactions: number;
  durationMs: number;
  createdAt: string;
}

export interface JobListResponse {
  items: ScrapeJob[];
  total: number;
  page: number;
  pageSize: number;
}

/** One job in trouble, with the account it belongs to already resolved — see GET /ScrapeJobs/problems. */
export interface ScrapeProblemAccount {
  ownerId: string;
  ownerName: string | null;
  ownerCompany: string | null;
  ownerEmail: string | null;
  ownerPhone: string | null;
  fleetCars: number;
  tollAccountId: string | null;
  tollAccountLogin: string | null;
  tollAccountNumber: string | null;
  tollAccountNickname: string | null;
  tollAccountActive: boolean | null;
}

/** Timeout = upstream was slow · Crashed = it threw · Retrying = re-queued after a failed attempt
 *  · Stuck = holding a lock too long · MfaRequired = waiting on a device · Degraded = partial run. */
export type ScrapeProblemKind = 'Timeout' | 'Crashed' | 'Retrying' | 'Stuck' | 'MfaRequired' | 'Degraded';

export interface ScrapeProblem {
  jobId: number;
  kind: ScrapeProblemKind;
  jobType: string;
  status: string;
  pluginKey: string | null;
  providerKey: string;
  attempts: number;
  maxAttempts: number;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  nextRetryAt: string | null;
  durationMs: number;
  transactionsFound: number;
  newTransactions: number;
  lockedBy: string | null;
  lockedAt: string | null;
  stalledMinutes: number | null;
  carBatchStart: number | null;
  carBatchSize: number | null;
  scrapeFromUtc: string;
  scrapeToUtc: string;
  account: ScrapeProblemAccount;
}

export interface ScrapeProblemsReport {
  generatedAtUtc: string;
  windowHours: number;
  summary: {
    total: number;
    byKind: Record<string, number>;
    byProvider: { provider: string; count: number }[];
    byOwner: {
      ownerId: string;
      ownerName: string | null;
      ownerCompany: string | null;
      ownerEmail: string | null;
      count: number;
      providers: string[];
    }[];
  };
  workers: {
    isPaused: boolean;
    shutdownRequested: boolean;
    total: number;
    alive: number;
    lastHeartbeatUtc: string | null;
  };
  problems: ScrapeProblem[];
}

export interface RegisteredWorker {
  workerId: string;
  status: string;
  isAlive: boolean;
  startedAtUtc: string;
  lastHeartbeatUtc: string;
  activeJobs: number;
  currentJobId: number | null;
  currentProvider: string | null;
  totalJobsProcessed: number;
  lastJobCompletedUtc: string | null;
  ipAddress: string | null;
  version: string | null;
}

export interface WorkersStatus {
  isPaused: boolean;
  pausedAtUtc: string | null;
  pausedBy: string | null;
  shutdownRequested: boolean;
  workers: RegisteredWorker[];
}

export interface GpsAccountStatus {
  accountId: string;
  ownerId: string;
  ownerEmail: string | null;
  providerId: string;
  nickname: string | null;
  isActive: boolean;
  isAlive: boolean;
  lastScrapeAtUtc: string | null;
  lastScrapeStatus: string | null;
  consecutiveFailures: number;
  nextEligibleScrapeUtc: string | null;
  updatedAt: string;
  trackerCount: number;
  activeTrackerCount: number;
}

export interface GpsAccountsStatus {
  accounts: GpsAccountStatus[];
}

export const scrapeJobsApi = {
  getDashboard: async (): Promise<ScrapeJobsDashboard> => {
    const response = await apiClient.get(`${BASE}/dashboard`);
    return response.data.result;
  },

  getJobs: async (params: {
    status?: string;
    jobType?: string;
    ownerId?: string;
    providerKey?: string;
    page?: number;
    pageSize?: number;
  }): Promise<JobListResponse> => {
    const response = await apiClient.get(`${BASE}/list`, { params });
    return response.data.result;
  },

  getProblems: async (params?: {
    hours?: number;
    jobType?: string;
    providerKey?: string;
    ownerId?: string;
    kind?: string;
    limit?: number;
  }): Promise<ScrapeProblemsReport> => {
    const response = await apiClient.get(`${BASE}/problems`, { params });
    return response.data.result;
  },

  triggerSchedule: async (): Promise<{ message: string; queuedJobs: number }> => {
    const response = await apiClient.post(`${BASE}/schedule`);
    return response.data.result;
  },

  resetFailed: async (providerKey?: string, ownerId?: string): Promise<{ reset: number }> => {
    const response = await apiClient.post(`${BASE}/reset-failed`, { providerKey, ownerId });
    return response.data.result;
  },

  cancelJobs: async (jobIds?: number[], providerKey?: string): Promise<{ cancelled: number }> => {
    const response = await apiClient.post(`${BASE}/cancel`, { jobIds, providerKey });
    return response.data.result;
  },

  clearCompleted: async (olderThanDays = 7): Promise<{ deleted: number }> => {
    const response = await apiClient.delete(`${BASE}/clear-completed`, { params: { olderThanDays } });
    return response.data.result;
  },

  enqueueManual: async (request: {
    jobType: string;
    ownerId: string;
    tollAccountId?: string;
    pluginKey?: string;
    providerKey: string;
    fromUtc?: string;
    toUtc?: string;
    priority?: number;
  }): Promise<{ jobId: number }> => {
    const response = await apiClient.post(`${BASE}/enqueue`, request);
    return response.data.result;
  },

  removeWorker: async (workerId: string): Promise<void> => {
    await apiClient.delete(`${BASE}/workers/${encodeURIComponent(workerId)}`);
  },

  purgeDeadWorkers: async (): Promise<{ removed: number }> => {
    const response = await apiClient.delete(`${BASE}/workers/dead`);
    return response.data.result;
  },

  // Worker control
  getWorkers: async (): Promise<WorkersStatus> => {
    const response = await apiClient.get(`${BASE}/workers`);
    return response.data.result;
  },

  getGpsAccounts: async (): Promise<GpsAccountsStatus> => {
    const response = await apiClient.get(`${BASE}/gps-accounts`);
    return response.data.result;
  },

  pauseWorkers: async (): Promise<void> => {
    await apiClient.post(`${BASE}/workers/pause`);
  },

  resumeWorkers: async (): Promise<void> => {
    await apiClient.post(`${BASE}/workers/resume`);
  },

  shutdownWorkers: async (): Promise<void> => {
    await apiClient.post(`${BASE}/workers/shutdown`);
  },

  restartWorkers: async (): Promise<void> => {
    await apiClient.post(`${BASE}/workers/restart`);
  },
};
